/**
 * Membership Stats two-row month grid (WSRADM-0035).
 *
 * Row A = New band counts; Row B = Returning band counts.
 * coveredMembers / netNewMembers are rendered as sent — never summed.
 * Hidden columns (WSRADM-0051) are omitted only — nothing replaces them.
 */
import { Fragment } from 'react';
import {
  Alert,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMonthLabel } from '@/api/reports/format';
import type {
  DurationColumn,
  MembershipBandCell,
  MembershipMonthRow,
} from '@/api/reports/types';
import type { ResourceStatus } from '@/hooks/useResource';
import { useCopy } from '@/hooks/useCopy';
import { ErrorDetails } from '@/components/ErrorDetails';
import { MEMBERSHIP_FIXED_KEYS, bandViewKey } from '@/config/viewPrefs';
import { CompareCaption, type CompareMode } from '@/components/CompareSupport';

const NONE_HIDDEN: ReadonlySet<string> = new Set();

const cellSx = { whiteSpace: 'nowrap' as const };
const rowACellSx = { whiteSpace: 'nowrap' as const, borderBottom: 'none' };
const rowLabelSx = {
  whiteSpace: 'nowrap' as const,
  color: 'text.secondary',
  fontSize: 12,
};

export interface MembershipMonthsTableProps {
  rows: readonly MembershipMonthRow[];
  durationColumns: readonly DurationColumn[];
  status: ResourceStatus;
  /** The load error, shown with copyable details when `status` is `'error'`. */
  error?: unknown;
  totalElements: number;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (size: number) => void;
  /** Column keys to omit (`lost` … `existing`, `band:<bandKey>`). */
  hidden?: ReadonlySet<string>;
  compare?: CompareMode;
}

/** Header label plus a keyboard-reachable info button whose tooltip explains the column. */
function ColumnHeaderHelp({ label, help }: { label: string; help: string }) {
  const { t } = useCopy();
  return (
    <>
      {label}
      <Tooltip title={help} describeChild>
        <IconButton
          size="small"
          aria-label={t('reports.membership.aboutColumn', 'About {{column}}', { column: label })}
          sx={{ ml: 0.25, p: 0.5 }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </>
  );
}

function bandCell(
  row: MembershipMonthRow,
  bandKey: string,
): MembershipBandCell | undefined {
  return row.bands.find((cell) => cell.bandKey === bandKey);
}

export function MembershipMonthsTable({
  rows,
  durationColumns,
  status,
  error,
  totalElements,
  page,
  size,
  onPageChange,
  onRowsPerPageChange,
  hidden = NONE_HIDDEN,
  compare = 'off',
}: MembershipMonthsTableProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const title = t('page.membershipStats.title', 'Membership Stats');
  const countFormatter = (val: number, mode: 'lastYear' | 'trailing3') =>
    mode === 'lastYear'
      ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }).format(val)
      : new Intl.NumberFormat(i18n.language, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(val);
  const show = (key: string) => !hidden.has(key);
  const visibleBands = durationColumns.filter((column) => show(bandViewKey(column.bandKey)));
  // The New / Returning row-label column and the second row exist only for bands.
  const showBands = visibleBands.length > 0;
  const monthRowSpan = showBands ? 2 : undefined;
  const fixedVisible = MEMBERSHIP_FIXED_KEYS.filter(show).length;
  const emptyColSpan = 1 + fixedVisible + (showBands ? 1 + visibleBands.length : 0);

  return (
    <>
      {status === 'loading' ? (
        <Skeleton variant="rounded" height={240} role="status" />
      ) : null}

      {status === 'error' ? (
        <Alert severity="error">
          {t('reports.loadError', 'Couldn’t load this report.')}
          <ErrorDetails
            intent="read"
            error={error}
            headline={t('reports.loadError', 'Couldn’t load this report.')}
          />
        </Alert>
      ) : null}

      {status === 'ready' ? (
        <>
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={title}>
              <TableHead>
                <TableRow>
                  <TableCell sx={cellSx}>
                    {t('reports.membership.columns.month', 'Month')}
                  </TableCell>
                  {show('lost') ? (
                    <TableCell sx={cellSx} align="right">
                      <ColumnHeaderHelp
                        label={t('reports.membership.columns.lost', 'Lost Members')}
                        help={t(
                          'reports.membership.help.lost',
                          'Members whose grace period ended this month without renewing.',
                        )}
                      />
                    </TableCell>
                  ) : null}
                  {show('lapsed') ? (
                    <TableCell sx={cellSx} align="right">
                      <ColumnHeaderHelp
                        label={t('reports.membership.columns.lapsed', 'Lapsed (in grace)')}
                        help={t(
                          'reports.membership.help.lapsed',
                          'Past their expiry date but still inside the grace period; they may still renew.',
                        )}
                      />
                    </TableCell>
                  ) : null}
                  {show('netMembers') ? (
                    <TableCell sx={cellSx} align="right">
                      <ColumnHeaderHelp
                        label={t('reports.membership.columns.netMembers', 'Net Members')}
                        help={t(
                          'reports.membership.help.netMembers',
                          'Members whose membership is current on the last day of the month (today, for this month).',
                        )}
                      />
                    </TableCell>
                  ) : null}
                  {show('netNew') ? (
                    <TableCell sx={cellSx} align="right">
                      <ColumnHeaderHelp
                        label={t('reports.membership.columns.netNew', 'Net New Members')}
                        help={t(
                          'reports.membership.help.netNew',
                          'New and returning members for the month.',
                        )}
                      />
                    </TableCell>
                  ) : null}
                  {show('existing') ? (
                    <TableCell sx={cellSx} align="right">
                      {t('reports.membership.columns.existing', 'Existing Members')}
                    </TableCell>
                  ) : null}
                  {showBands ? <TableCell sx={cellSx} /> : null}
                  {visibleBands.map((column) => (
                    <TableCell key={column.bandKey} sx={cellSx} align="right">
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={emptyColSpan} sx={cellSx}>
                      <Typography variant="body2" color="text.secondary">
                        {t('reports.empty', 'No months to show.')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => {
                    const monthLabel = formatMonthLabel(row.month, i18n.language);
                    return (
                      <Fragment key={row.month}>
                        <TableRow>
                          <TableCell sx={cellSx} rowSpan={monthRowSpan}>
                            <Link
                              component={RouterLink}
                              to={`/reports/membership/${row.month}`}
                            >
                              {monthLabel}
                            </Link>
                          </TableCell>
                          {show('lost') ? (
                            <TableCell sx={cellSx} align="right" rowSpan={monthRowSpan}>
                              {`(${row.lost})`}
                              <CompareCaption
                                comparisons={row.comparisons}
                                mode={compare}
                                getMetric={(v) => v.lost}
                                formatValue={countFormatter}
                                testId="lost"
                              />
                            </TableCell>
                          ) : null}
                          {show('lapsed') ? (
                            <TableCell sx={cellSx} align="right" rowSpan={monthRowSpan}>
                              {row.lapsed > 0 ? (
                                <Link
                                  component={RouterLink}
                                  to={`/reports/membership/${row.month}?tab=lapsed`}
                                >
                                  {row.lapsed}
                                </Link>
                              ) : (
                                row.lapsed
                              )}
                              <CompareCaption
                                comparisons={row.comparisons}
                                mode={compare}
                                getMetric={(v) => v.lapsed}
                                formatValue={countFormatter}
                                testId="lapsed"
                              />
                            </TableCell>
                          ) : null}
                          {show('netMembers') ? (
                            <TableCell sx={cellSx} align="right" rowSpan={monthRowSpan}>
                              {row.coveredMembers}
                              <CompareCaption
                                comparisons={row.comparisons}
                                mode={compare}
                                getMetric={(v) => v.coveredMembers}
                                formatValue={countFormatter}
                                testId="coveredMembers"
                              />
                            </TableCell>
                          ) : null}
                          {show('netNew') ? (
                            <TableCell sx={cellSx} align="right" rowSpan={monthRowSpan}>
                              {row.netNewMembers}
                              <CompareCaption
                                comparisons={row.comparisons}
                                mode={compare}
                                getMetric={(v) => v.netNewMembers}
                                formatValue={countFormatter}
                                testId="netNewMembers"
                              />
                            </TableCell>
                          ) : null}
                          {show('existing') ? (
                            <TableCell sx={cellSx} align="right" rowSpan={monthRowSpan}>
                              {row.existing}
                              <CompareCaption
                                comparisons={row.comparisons}
                                mode={compare}
                                getMetric={(v) => v.existing}
                                formatValue={countFormatter}
                                testId="existing"
                              />
                            </TableCell>
                          ) : null}
                          {showBands ? (
                            <TableCell sx={{ ...rowACellSx, ...rowLabelSx }}>
                              {t('reports.membership.rowNew', 'New')}
                            </TableCell>
                          ) : null}
                          {visibleBands.map((column) => {
                            const cell = bandCell(row, column.bandKey);
                            return (
                              <TableCell
                                key={`${row.month}-new-${column.bandKey}`}
                                sx={rowACellSx}
                                align="right"
                              >
                                {cell?.new ?? 0}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                        {showBands ? (
                          <TableRow>
                            <TableCell sx={{ ...cellSx, ...rowLabelSx }}>
                              {t('reports.membership.rowReturning', 'Returning')}
                            </TableCell>
                            {visibleBands.map((column) => {
                              const cell = bandCell(row, column.bandKey);
                              return (
                                <TableCell
                                  key={`${row.month}-returning-${column.bandKey}`}
                                  sx={cellSx}
                                  align="right"
                                >
                                  {cell?.returning ?? 0}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ) : null}
                      </Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={(_event, next) => onPageChange(next)}
            rowsPerPage={size}
            onRowsPerPageChange={(event) => {
              onRowsPerPageChange(Number(event.target.value));
            }}
            rowsPerPageOptions={[24, 50, 100]}
          />
        </>
      ) : null}
    </>
  );
}
